import {
  PROJECT_PRODUCT,
  AUTO_SAVE_REQUEST_TYPE,
  CONTENT_FIELDS_BY_PRODUCT,
  STORE_TO_API_FIELD,
} from '@src/constants/project';
import {
  normalizeNote,
  isVideoSettingsChanged,
  isOwnCaptionStyle,
  buildCaptionConfigPayload,
  isVideoSource,
} from '@src/services/project';

const getContentFields = (product) =>
  CONTENT_FIELDS_BY_PRODUCT[product] ||
  CONTENT_FIELDS_BY_PRODUCT[PROJECT_PRODUCT.TTS];

// `currentNote` (Presentation) is compared/saved in its normalized (trimmed)
// form so whitespace-only edits aren't treated as changes.
const getFieldValue = (block, field) =>
  field === 'currentNote' ? normalizeNote(block?.currentNote) : block?.[field];

// Value used when serializing a block for the save payload. `emphasis` is only
// meaningful for voices that support it; for the rest, return `undefined` so the
// serializer omits `emphasisIntensity` entirely instead of sending 0 (mirrors
// the gating in buildBlockCreatePayload for the add-block path).
const getSaveFieldValue = (block, field) => {
  if (field === 'emphasis' && !block?.hasEmphasis) return undefined;
  return getFieldValue(block, field);
};

export const pickBlockContent = (block, product) =>
  Object.fromEntries(
    getContentFields(product).map((field) => [
      field,
      getFieldValue(block, field),
    ]),
  );

// Snapshot the content of the changed blocks as { id -> content }.
// Used as the new `lastSaved` baseline after a successful save.
export const pickChangedBlocksContent = (changedIds, blocks, product) =>
  Object.fromEntries(
    changedIds.map((id) => [id, pickBlockContent(blocks[id], product)]),
  );

// Treat `undefined` and `null` as the same "empty" value. The undo/redo path
// restores blocks through the history snapshot overlay, which coerces optional
// reference fields (translatedText, startTime, endTime, speakerId, …) to `null`
// via `?? null`, whereas the auto-save baseline may hold `undefined`. Without
// this, every block with such a field would look "changed" after an undo and
// the whole project would be re-sent.
const isNullish = (value) => value === undefined || value === null;
const fieldValuesEqual = (a, b) => (isNullish(a) && isNullish(b)) || a === b;

// The content fields that actually differ from the saved baseline. When there
// is no baseline entry, every content field is considered changed.
const getChangedFields = (block, savedContent, product) =>
  getContentFields(product).filter((field) =>
    savedContent
      ? !fieldValuesEqual(getFieldValue(block, field), savedContent[field])
      : true,
  );

export const isBlockContentChanged = (block, savedContent, product) =>
  getChangedFields(block, savedContent, product).length > 0;

const arraysEqual = (a, b) =>
  a.length === b.length && a.every((value, i) => value === b[i]);

// A music clip (editor shape) → the stored musicBlock shape. Seconds throughout.
// The picker's 0..100 volume maps to the Backend `gain` (0 = mute, 1 = full).
// The editor has no boost, so gain stays in 0..1 of the Backend's 0..4 range.
export const musicClipsToMusicBlocks = (clips = []) =>
  clips.map((clip) => ({
    id: clip.id,
    url: clip.link,
    label: clip.name,
    startTime: clip.startSec,
    duration: clip.endSec - clip.startSec + clip.trimStart,
    trimStart: clip.trimStart || 0,
    gain: (clip.volume ?? 100) / 100,
  }));

// Level a clip with no stored gain (older projects) reopens at, on the picker's
// 0..100 scale.
const DEFAULT_MUSIC_CLIP_VOLUME = 100;

// Stored `gain` (0..4) → the picker's 0..100 volume, capped at 100 (no boost in
// the editor). `== null` keeps a real 0 (mute) instead of defaulting it to full.
const gainToVolume = (gain) =>
  gain == null
    ? DEFAULT_MUSIC_CLIP_VOLUME
    : Math.min(100, Math.round(gain * 100));

// Stored musicBlock → editor musicClip shape (inverse of the above). The stored
// id keys as `_id`; the API camelCases it to `id`, so accept either.
export const musicBlocksToMusicClips = (blocks = []) =>
  blocks.map((block) => ({
    // eslint-disable-next-line no-underscore-dangle
    id: block.id ?? block._id,
    link: block.url,
    name: block.label,
    volume: gainToVolume(block.gain),
    trimStart: block.trimStart || 0,
    startSec: block.startTime,
    endSec: block.startTime + block.duration - block.trimStart,
  }));

const musicBlocksEqual = (a, b) =>
  a.length === b.length &&
  a.every((block, i) => {
    const saved = b[i];
    return (
      saved &&
      block.id === saved.id &&
      block.url === saved.url &&
      block.label === saved.label &&
      block.startTime === saved.startTime &&
      block.duration === saved.duration &&
      block.trimStart === saved.trimStart &&
      block.gain === saved.gain
    );
  });

// Track levels (`project.volumes`, 0..1) → the stored `volumes` shape (0..100 %).
// Same keys on both sides; missing values fall back to full.
export const trackVolumesToStored = ({ voiceover, media, music } = {}) => ({
  voiceover: Math.round((voiceover ?? 1) * 100),
  media: Math.round((media ?? 1) * 100),
  music: Math.round((music ?? 1) * 100),
});

const volumesEqual = (a, b) =>
  !!b &&
  a.voiceover === b.voiceover &&
  a.media === b.media &&
  a.music === b.music;

const getChangedCaptionConfig = (videoSettings, lastSaved) => {
  const captionStyle = videoSettings?.captionStyle;
  if (!isOwnCaptionStyle(captionStyle?.styleId)) return undefined;
  const config =
    captionStyle.config && buildCaptionConfigPayload(captionStyle.config);
  if (!config) return undefined;
  const saved = lastSaved?.videoSettings?.captionStyle?.config;
  const savedConfig = saved && buildCaptionConfigPayload(saved);
  if (savedConfig && JSON.stringify(config) === JSON.stringify(savedConfig)) {
    return undefined;
  }
  return { styleId: captionStyle.styleId, config };
};

// Project-level config fields that auto-save tracks (sent via PATCH /projects/:id).
// videoSettings is compared in its raw (store) form here; the saga turns it into
// the Backend payload before sending, and the baseline keeps the raw form.
const computeConfigMeta = ({
  product,
  title,
  audioType,
  videoSettings,
  musicClips,
  volumes,
  lastSaved,
}) => {
  const meta = {};
  if (title !== lastSaved?.title) meta.title = title;
  if (audioType !== lastSaved?.audioType) meta.audioType = audioType;
  if (product === PROJECT_PRODUCT.DUBBING) {
    if (isVideoSettingsChanged(videoSettings, lastSaved?.videoSettings)) {
      meta.videoSettings = videoSettings;
    }
    const musicBlocks = musicClipsToMusicBlocks(musicClips);
    if (!musicBlocksEqual(musicBlocks, lastSaved?.musicBlocks || [])) {
      meta.musicBlocks = musicBlocks;
    }
    const storedVolumes = trackVolumesToStored(volumes);
    if (!volumesEqual(storedVolumes, lastSaved?.volumes)) {
      meta.volumes = storedVolumes;
    }
  }
  return meta;
};

// Group consecutive added blocks (ids not in the baseline) into batches, each
// anchored to the block right before it: `afterBlockId` is the surviving block
// the batch follows, or `null` for a batch at the very start of the project.
//
// The add-block API inserts a batch right after its anchor, so adding each batch
// at its own anchor reproduces the exact current order on the Backend — no
// follow-up reorder — and keeps every block at a timestamp-valid position
// (Dubbing BE validates insert position against timestamps). The block before a
// maximal batch is never itself added, so the anchor is always a surviving block.
const buildAddedBlockBatches = (currentBlockIds, savedIdSet) => {
  const batches = [];
  currentBlockIds.forEach((id, index) => {
    if (savedIdSet.has(id)) return; // surviving block, not an add
    const prevId = index > 0 ? currentBlockIds[index - 1] : null;
    const startsNewBatch = prevId === null || savedIdSet.has(prevId);
    if (startsNewBatch) batches.push({ afterBlockId: prevId, ids: [id] });
    else batches[batches.length - 1].ids.push(id);
  });
  return batches;
};

export const computeAutoSaveDiff = ({
  product,
  blockIds,
  blocks,
  lastSaved,
  title,
  audioType,
  videoSettings,
  musicClips,
  volumes,
  source,
}) => {
  const savedContentById = lastSaved?.blocks || {};
  const savedBlockIds = lastSaved?.blockIds || [];
  const currentBlockIds = blockIds || [];
  const savedIdSet = new Set(savedBlockIds);
  const currentIdSet = new Set(currentBlockIds);
  // Per changed block, record exactly which fields differ so the save payload
  // carries only those (not the whole block content).
  const changedFieldsById = {};
  const changedIds = currentBlockIds.filter((id) => {
    if (!savedIdSet.has(id)) return false;
    const fields = getChangedFields(blocks[id], savedContentById[id], product);
    if (fields.length === 0) return false;
    changedFieldsById[id] = fields;
    return true;
  });
  // Baseline blocks that are no longer present -> deleted by the user.
  const removedIds = savedBlockIds.filter((id) => !currentIdSet.has(id));
  // Current blocks not in the baseline -> newly added by the user.
  const addedIds = currentBlockIds.filter((id) => !savedIdSet.has(id));

  // One ADD request per contiguous batch, each at its own anchor (see helper).
  const addedBatches = buildAddedBlockBatches(currentBlockIds, savedIdSet);

  // A reorder is only needed when the SURVIVING blocks changed their relative
  // order vs the baseline. Added blocks are already placed correctly by their
  // per-batch anchor, so an add alone never triggers a reorder.
  const survivorsInCurrentOrder = currentBlockIds.filter((id) =>
    savedIdSet.has(id),
  );
  const survivorsInBaselineOrder = savedBlockIds.filter((id) =>
    currentIdSet.has(id),
  );
  const orderChanged = !arraysEqual(
    survivorsInCurrentOrder,
    survivorsInBaselineOrder,
  );

  const meta = computeConfigMeta({
    product,
    title,
    audioType,
    videoSettings,
    musicClips,
    volumes,
    lastSaved,
  });
  const metaChanged = Object.keys(meta).length > 0;

  // Only a video has captions. Dubbing also covers SRT projects, and a store
  // that has held a video project keeps its videoSettings after one is closed.
  const captionConfig =
    product === PROJECT_PRODUCT.DUBBING && isVideoSource(source)
      ? getChangedCaptionConfig(videoSettings, lastSaved)
      : undefined;

  const isDirty =
    changedIds.length > 0 ||
    removedIds.length > 0 ||
    addedIds.length > 0 ||
    orderChanged ||
    metaChanged ||
    Boolean(captionConfig);
  return {
    changedIds,
    changedFieldsById,
    removedIds,
    addedIds,
    addedBatches,
    orderChanged,
    meta,
    metaChanged,
    captionConfig,
    isDirty,
  };
};

export const planAutoSaveRequests = (
  diff,
  { blockIds, blocks, serializeBlock, serializeNewBlock },
) => {
  const requests = [];
  // First: the style document has to exist before the project can name it.
  if (diff.captionConfig) {
    requests.push({
      type: AUTO_SAVE_REQUEST_TYPE.SAVE_CAPTION_CONFIG,
      payload: diff.captionConfig,
    });
  }
  if (diff.metaChanged) {
    requests.push({
      type: AUTO_SAVE_REQUEST_TYPE.UPDATE_CONFIG,
      payload: diff.meta,
    });
  }
  // Delete before adding so the Backend reaches the on-screen result directly:
  // e.g. deleting every block then adding a fresh one must NOT insert the new
  // block among the old ones first (for Dubbing that fails timestamp validation).
  // Anchors (`afterBlockId`) are always surviving blocks, so they are never
  // removed by this delete.
  if (diff.removedIds?.length) {
    requests.push({
      type: AUTO_SAVE_REQUEST_TYPE.DELETE_BLOCKS,
      payload: diff.removedIds,
    });
  }
  // Update surviving blocks BEFORE adding new ones. Undoing a Dubbing merge
  // re-adds the split-off block while shrinking the merged block back to its
  // original time range; the Backend validates a new block's timestamps against
  // its neighbours, so the neighbour must be updated first or the add fails
  // validation (it would still overlap the not-yet-shrunk merged block).
  if (diff.changedIds.length) {
    // Send only the fields that changed for each block (plus its id).
    const changedBlocksPayload = diff.changedIds.map((id) => ({
      id,
      ...serializeBlock(blocks[id], diff.changedFieldsById?.[id]),
    }));
    requests.push({
      type: AUTO_SAVE_REQUEST_TYPE.UPDATE_BLOCK_FIELDS,
      payload: changedBlocksPayload,
    });
  }
  // Then create new blocks (so a later REORDER can reference them). One ADD per
  // contiguous batch, each anchored to its own `afterBlockId` (absent → start of
  // project), so blocks land at their exact positions and no reorder is needed.
  (diff.addedBatches || []).forEach((batch) => {
    if (!batch.ids.length) return;
    const payload = {
      blocks: batch.ids.map((id) => serializeNewBlock(blocks[id], id)),
    };
    if (batch.afterBlockId != null) payload.afterBlockId = batch.afterBlockId;
    requests.push({
      type: AUTO_SAVE_REQUEST_TYPE.ADD_BLOCKS,
      payload,
    });
  });
  // Reorder last: DELETE/ADD settle the id set first, then send the full order.
  if (diff.orderChanged) {
    requests.push({
      type: AUTO_SAVE_REQUEST_TYPE.REORDER_BLOCKS,
      payload: blockIds,
    });
  }
  return requests;
};

// Serialize a block for the save payload. `fields` restricts output to the
// given store-field names (the changed ones); when omitted, all content fields
// for the product are serialized (e.g. for newly added blocks).
export const serializeBlockForSave = (block, product, fields) =>
  Object.fromEntries(
    (fields || getContentFields(product))
      .map((field) => [
        STORE_TO_API_FIELD[field] || field,
        getSaveFieldValue(block, field),
      ])
      .filter(([, value]) => value !== undefined),
  );
